import React from "react";
import { useTranslation } from "react-i18next";
import { detectFileType, isHtmlFile } from "@/utils/fileTypeDetection";
import { getFileTypeIcon } from "@/utils/fileHelpers";
import Icon, { ICON_SIZES } from "../../library/Icon";
import InlinePdfPreview from "./InlinePdfPreview";
import InlineDocPreview from "./InlineDocPreview";
import InlineExcelPreview from "./InlineExcelPreview";
import InlineTextPreview from "./InlineTextPreview";
import InlineAudioPreview from "./InlineAudioPreview";
import InlineVideoPreview from "./InlineVideoPreview";
import InlinePptxPreview from "./InlinePptxPreview";
import InlineHtmlPreview from "./InlineHtmlPreview";

const FilePreviewContent: React.FC<{
   fileType: ReturnType<typeof detectFileType>;
   dataUrl: string;
   blobUrl: string | null;
   excelData: string[][] | null;
   fileName: string;
   mimeType: string;
   /** Optional remote URL (e.g. signed download link). When set, takes precedence over the base64-derived URLs. */
   url?: string;
}> = ({ fileType, dataUrl, blobUrl, excelData, fileName, mimeType, url }) => {
   const { t } = useTranslation("ask");
   // Remote URL wins over the locally-derived blob; images alone can also use
   // the base64 data URL so they can render without a blob round-trip.
   const remote = url ?? blobUrl;
   const imgSrc = url ?? dataUrl;

   if (isHtmlFile(mimeType, fileName) && remote) {
      return <InlineHtmlPreview key={remote} url={remote} fileName={fileName} />;
   }
   if (fileType.isPdf && remote) return <InlinePdfPreview url={remote} />;
   if (fileType.isImage) {
      return (
         <div className="flex items-center justify-center p-4 h-full">
            <img src={imgSrc} alt={fileName} className="max-w-full max-h-full object-contain" />
         </div>
      );
   }
   if (fileType.isDocx && remote) return <InlineDocPreview url={remote} />;
   if (fileType.isExcel && (remote || excelData)) {
      return <InlineExcelPreview data={excelData} url={remote ?? undefined} />;
   }
   if (fileType.isPptx) return <InlinePptxPreview fileName={fileName} />;
   if (fileType.isAudio && remote) {
      return <InlineAudioPreview url={remote} mimeType={mimeType} fileName={fileName} />;
   }
   if (fileType.isVideo && remote) {
      return <InlineVideoPreview url={remote} mimeType={mimeType} />;
   }
   if (fileType.isText && remote) {
      return <InlineTextPreview url={remote} fileName={fileName} mimeType={mimeType} />;
   }
   return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
         <Icon icon={getFileTypeIcon(fileName, mimeType)} size={ICON_SIZES.XXL} className="text-400" />
         <p className="text-xs text-500">{t("preview.notAvailable")}</p>
      </div>
   );
};

export default FilePreviewContent;
